import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import {
  Send, Sparkles, Loader2, Plus, Search, MessageSquare, MoreVertical,
  Pencil, Trash2, Check, X, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const SUGGESTIONS = [
  "Which of my patients are at highest no-show risk this week?",
  "Draft a follow-up SMS for a completed root canal.",
  "Give me a differential for cold-sensitivity in tooth 14.",
  "Summarize revenue trends and one operational fix.",
];

const ACTIVE_SESSION_KEY = "viyora_copilot_active_session";

export default function AICommand() {
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patient") || null;
  const seedQuery = searchParams.get("q") || "";
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(true);
  const [search, setSearch] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  // Load sessions on mount, pick the active one (last stored or most recent).
  const loadSessions = async (opts = {}) => {
    try {
      const { data } = await api.get("/copilot/sessions");
      setSessions(data || []);
      return data || [];
    } catch (err) {
      if (!opts.silent) toast.error("Failed to load conversations");
      return [];
    }
  };

  const loadMessages = async (sid) => {
    if (!sid) { setMessages([]); return; }
    setHydrating(true);
    try {
      const { data } = await api.get(`/ai/history/${sid}`);
      setMessages((data || []).map((m) => ({ role: m.role, content: m.content })));
    } catch {
      setMessages([]);
    } finally {
      setHydrating(false);
    }
  };

  // Boot sequence: fetch sessions, pick active, hydrate messages.
  useEffect(() => {
    (async () => {
      const list = await loadSessions({ silent: true });
      const savedId = (() => {
        try { return localStorage.getItem(ACTIVE_SESSION_KEY); } catch { return null; }
      })();
      const preferred = list.find((s) => s.id === savedId) || list[0];
      if (preferred) {
        setActiveId(preferred.id);
        loadMessages(preferred.id);
      } else {
        // No sessions yet — create the first one lazily on first send.
        setHydrating(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Persist active session id
  useEffect(() => {
    if (activeId) {
      try { localStorage.setItem(ACTIVE_SESSION_KEY, activeId); } catch { /* noop */ }
    }
  }, [activeId]);

  // Seed the composer with an incoming ?q= (e.g. from Dental Chart's Ask Copilot).
  // Only runs once — clears the URL so a manual refresh doesn't re-seed.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !seedQuery || hydrating) return;
    seededRef.current = true;
    setInput(seedQuery);
  }, [seedQuery, hydrating]);

  const switchTo = async (sid) => {
    if (sid === activeId) return;
    setActiveId(sid);
    await loadMessages(sid);
  };

  const newChat = async () => {
    try {
      const { data } = await api.post("/copilot/sessions", { title: "New conversation" });
      setSessions((prev) => [data, ...prev.filter((s) => s.id !== data.id)]);
      setActiveId(data.id);
      setMessages([]);
    } catch (err) {
      toast.error("Failed to start a new conversation");
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    let sid = activeId;
    // If no active session, create one now.
    if (!sid) {
      try {
        const { data } = await api.post("/copilot/sessions", { title: "New conversation" });
        setSessions((prev) => [data, ...prev]);
        setActiveId(data.id);
        sid = data.id;
      } catch {
        toast.error("Could not start conversation");
        return;
      }
    }
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/command", {
        session_id: sid, message: msg,
        ...(patientId ? { patient_id: patientId } : {}),
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      // Refresh session list to bring active session to top + update auto-title.
      loadSessions({ silent: true });
    } catch (e) {
      toast.error(apiError(e, "AI request failed"));
    } finally {
      setLoading(false);
    }
  };

  const beginRename = (s) => { setRenamingId(s.id); setRenameValue(s.title || ""); };
  const cancelRename = () => { setRenamingId(null); setRenameValue(""); };
  const commitRename = async (sid) => {
    const title = renameValue.trim();
    if (!title) return cancelRename();
    try {
      await api.patch(`/copilot/sessions/${sid}`, { title });
      setSessions((prev) => prev.map((s) => (s.id === sid ? { ...s, title } : s)));
      toast.success("Renamed");
    } catch { toast.error("Rename failed"); }
    finally { cancelRename(); }
  };

  const deleteSession = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? This removes the conversation and all its messages.`)) return;
    try {
      await api.delete(`/copilot/sessions/${s.id}`);
      const remaining = sessions.filter((x) => x.id !== s.id);
      setSessions(remaining);
      if (activeId === s.id) {
        const next = remaining[0];
        if (next) { setActiveId(next.id); loadMessages(next.id); }
        else { setActiveId(null); setMessages([]); }
      }
      toast.success("Conversation deleted");
    } catch { toast.error("Delete failed"); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) => (s.title || "").toLowerCase().includes(q));
  }, [sessions, search]);

  return (
    <div className="h-[calc(100vh-8rem)] grid grid-cols-1 lg:grid-cols-4 gap-4">
      {/* Sidebar */}
      <aside className={`card-widget flex flex-col ${sidebarOpen ? "lg:col-span-1" : "hidden lg:flex lg:col-span-0 lg:w-14"}`}
        data-testid="copilot-sidebar">
        <div className="flex items-center justify-between gap-2 mb-3">
          {sidebarOpen && <div className="overline text-ink-soft">Conversations</div>}
          <button
            data-testid="copilot-sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-ink-soft hover:text-ink p-1"
            title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarOpen ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
          </button>
        </div>
        {sidebarOpen && (
          <>
            <button
              data-testid="copilot-new-chat"
              onClick={newChat}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium bg-pine text-linen hover:bg-pine-mid transition-colors mb-3"
            >
              <Plus className="w-4 h-4" /> New chat
            </button>
            <div className="relative mb-3">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input
                data-testid="copilot-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search conversations…"
                className="w-full rounded-full border border-[#E8EAE9] pl-8 pr-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-pine/30"
              />
            </div>
            <div className="flex-1 overflow-auto space-y-1 pr-1" data-testid="copilot-sessions-list">
              {filtered.length === 0 && (
                <div className="text-xs text-ink-soft text-center py-6">
                  {sessions.length === 0 ? "No conversations yet. Start by asking a question." : "No matches."}
                </div>
              )}
              {filtered.map((s) => {
                const isActive = s.id === activeId;
                const isRenaming = renamingId === s.id;
                return (
                  <div key={s.id} data-testid={`copilot-session-${s.id}`}
                    className={`group rounded-xl px-2.5 py-2 flex items-center gap-2 cursor-pointer transition-colors ${
                      isActive ? "bg-pine text-linen" : "hover:bg-linen text-ink"
                    }`}
                    onClick={() => !isRenaming && switchTo(s.id)}
                  >
                    <MessageSquare className={`w-3.5 h-3.5 shrink-0 ${isActive ? "opacity-90" : "opacity-50"}`} />
                    <div className="flex-1 min-w-0">
                      {isRenaming ? (
                        <div className="flex items-center gap-1">
                          <input
                            data-testid={`copilot-rename-input-${s.id}`}
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitRename(s.id);
                              if (e.key === "Escape") cancelRename();
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 min-w-0 bg-white text-ink border border-[#E8EAE9] rounded-md px-2 py-0.5 text-xs"
                          />
                          <button onClick={(e) => { e.stopPropagation(); commitRename(s.id); }}
                            data-testid={`copilot-rename-save-${s.id}`}
                            className="text-ink hover:text-ink-mid p-0.5">
                            <Check className="w-3 h-3" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); cancelRename(); }}
                            className="text-ink-soft hover:text-ink p-0.5">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="text-xs font-medium truncate">{s.title || "Untitled"}</div>
                      )}
                    </div>
                    {!isRenaming && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`copilot-session-menu-${s.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className={`p-0.5 rounded opacity-0 group-hover:opacity-100 ${isActive ? "text-linen/80" : "text-ink-mid"}`}
                          >
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); beginRename(s); }}
                            data-testid={`copilot-rename-${s.id}`}>
                            <Pencil className="w-3.5 h-3.5 mr-2" /> Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); deleteSession(s); }}
                            data-testid={`copilot-delete-${s.id}`}
                            className="text-red-600 focus:text-red-700">
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </aside>

      {/* Chat pane */}
      <div className={`card-widget flex flex-col ${sidebarOpen ? "lg:col-span-3" : "lg:col-span-4"}`}>
        <div className="flex items-center gap-2 border-b border-[#E8EAE9] pb-3 mb-3">
          <Sparkles className="w-4 h-4 text-ink" />
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg leading-none truncate">
              {sessions.find((s) => s.id === activeId)?.title || "Viyora Copilot"}
            </div>
            <div className="text-xs text-ink-mid">Powered by Viyora AI</div>
          </div>
        </div>
        <div className="flex-1 overflow-auto space-y-4 pr-2" data-testid="ai-messages">
          {hydrating && messages.length === 0 && (
            <div className="h-full grid place-items-center text-center text-ink-soft text-xs">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          )}
          {!hydrating && messages.length === 0 && (
            <div className="h-full grid place-items-center text-center text-ink-soft text-sm px-6">
              <div className="max-w-md space-y-4">
                <div className="font-display text-2xl text-ink">Ready when you are.</div>
                <div>Ask about no-show risk, treatment planning, or clinic operations.</div>
                <div className="grid grid-cols-1 gap-2 mt-4">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={`sug-${i}`} data-testid={`suggestion-${i}`} onClick={() => send(s)}
                      className="text-left w-full text-xs p-3 rounded-xl bg-linen hover:bg-linen-dark transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={`msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
                m.role === "user" ? "bg-pine text-linen rounded-br-sm" : "bg-linen text-ink rounded-bl-sm"
              }`} data-testid={`msg-${m.role}-${i}`}>{m.content}</div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-linen rounded-2xl px-4 py-3 text-sm text-ink-mid inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2" data-testid="ai-input-form">
          <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the copilot…"
            className="flex-1 rounded-full border border-[#E8EAE9] bg-white px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
          <button data-testid="ai-send" disabled={loading} className="btn-primary inline-flex items-center gap-2 text-sm">
            <Send className="w-4 h-4" /> Send
          </button>
        </form>
      </div>
    </div>
  );
}
